// src/api/CompanyApi.js
import axios from 'axios';
import { ERROR_CODES } from '../constants/errors';

const baseUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
const baseApiUrl = `${baseUrl}/company/api`; // Consistent base URL

/**
 * Fetches company data from the backend.
 * Includes 'logoUrlToDisplay' which points to external URL or internal /logo endpoint.
 * Also includes 'dbLogoURL' holding the raw value from the logoURL column.
 * @param {string} token - Firebase Auth ID token.
 * @returns {Promise<object>} - { status: boolean, data: object | null, error?: string, message?: string }
 */
export const apiFetchCompanyData = async (token) => {
    const url = `${baseApiUrl}/data`;
    console.log(`[apiFetchCompanyData] Fetching company data from ${url}`);
    try {
        const response = await axios.get(url, {
            headers: { Authorization: `Bearer ${token}` },
            validateStatus: (status) => status >= 200 && status < 500,
        });

        if (response.status === 200) {
            console.log("[apiFetchCompanyData] Success:", response.data);
            const data = response.data && typeof response.data === 'object' ? response.data : {};
            return { status: true, data: data };
        } else {
            console.error("[apiFetchCompanyData] API Error:", response.status, response.data);
            return {
                status: false,
                error: response.data?.error || `HTTP_ERROR_${response.status}`,
                message: response.data?.message || "Failed to fetch company data.",
                data: null
            };
        }
    } catch (error) {
        console.error("[apiFetchCompanyData] Network or request error:", error);
        // ... (standard error handling as before)
        if (error.response) {
             return { status: false, error: error.response.data?.error || `HTTP_ERROR_${error.response.status}`, message: error.response.data?.message || "API request failed.", data: null };
        } else if (error.request) {
             return { status: false, error: ERROR_CODES.NETWORK_ERROR, message: "No response received from server.", data: null };
        } else {
             return { status: false, error: ERROR_CODES.REQUEST_ERROR, message: error.message || "Error setting up request.", data: null };
        }
    }
};

/**
 * Updates (or creates) company data. Accepts JSON or FormData.
 * @param {string} token - Firebase Auth ID token.
 * @param {object | FormData} payload - Either JSON { companyName, ..., logoURL } or FormData containing fields and optionally 'logoFile'.
 * @returns {Promise<object>} - { status: boolean, message?: string, error?: string }
 */
export const apiUpdateCompanyData = async (token, payload) => {
    const url = `${baseApiUrl}/update`;
    console.log(`[apiUpdateCompanyData] Updating company data at ${url}. Payload type: ${payload instanceof FormData ? 'FormData' : 'JSON'}`);

    let config = {
        headers: {
            Authorization: `Bearer ${token}`,
            // Content-Type is set automatically by Axios for FormData
            // For JSON, we set it explicitly.
        },
        validateStatus: (status) => status >= 200 && status < 500,
    };

    let dataToSend = payload;

    // Adjust headers and potentially data format based on payload type
    if (!(payload instanceof FormData)) {
        // If it's not FormData, assume it's a JSON object
        config.headers['Content-Type'] = 'application/json';
        // No change needed for dataToSend if it's already the correct JSON object
    } else {
         // Ensure Content-Type is NOT manually set for FormData
         // delete config.headers['Content-Type']; // Axios handles this
    }


    try {
        const response = await axios.post(url, dataToSend, config);

        if (response.status === 200) {
            console.log("[apiUpdateCompanyData] Success:", response.data);
            return { status: true, message: response.data?.message || "Company data updated successfully." };
        } else {
            console.error("[apiUpdateCompanyData] API Error:", response.status, response.data);
            return {
                status: false,
                error: response.data?.error || `HTTP_ERROR_${response.status}`,
                message: response.data?.message || "Failed to update company data.",
            };
        }
    } catch (error) {
        console.error("[apiUpdateCompanyData] Network or request error:", error);
        // ... (standard error handling as before)
         if (error.response) {
            return { status: false, error: error.response.data?.error || `HTTP_ERROR_${error.response.status}`, message: error.response.data?.message || "API request failed." };
        } else if (error.request) {
            return { status: false, error: ERROR_CODES.NETWORK_ERROR, message: "No response received from server." };
        } else {
            return { status: false, error: ERROR_CODES.REQUEST_ERROR, message: error.message || "Error setting up request." };
        }
    }
};

/**
 * Fetches public company info (name, logo URL) without authentication.
 * @returns {Promise<object>} - { status: boolean, data: { companyName, logoUrlToDisplay } | null, error?: string, message?: string }
 */
export const apiFetchPublicCompanyInfo = async () => {
    const url = `${baseUrl}/public/api/company/info`; // Use the new public endpoint
    console.log(`[apiFetchPublicCompanyInfo] Fetching public company info from ${url}`);
    try {
        const response = await axios.get(url, {
            // No Authorization header needed
            validateStatus: (status) => status >= 200 && status < 500,
        });

        if (response.status === 200) {
            console.log("[apiFetchPublicCompanyInfo] Success:", response.data);
            const data = response.data && typeof response.data === 'object' ? response.data : {};
            // Ensure data structure even if backend sends empty {}
            return { status: true, data: { companyName: data.companyName || null, logoUrlToDisplay: data.logoUrlToDisplay || null } };
        } else {
            console.error("[apiFetchPublicCompanyInfo] API Error:", response.status, response.data);
            return {
                status: false,
                error: response.data?.error || `HTTP_ERROR_${response.status}`,
                message: response.data?.message || "Failed to fetch public company info.",
                data: null
            };
        }
    } catch (error) {
        console.error("[apiFetchPublicCompanyInfo] Network or request error:", error);
        if (error.response) {
            return { status: false, error: error.response.data?.error || `HTTP_ERROR_${error.response.status}`, message: error.response.data?.message || "API request failed.", data: null };
        } else if (error.request) {
            return { status: false, error: ERROR_CODES.NETWORK_ERROR, message: "No response received from server.", data: null };
        } else {
            return { status: false, error: ERROR_CODES.REQUEST_ERROR, message: error.message || "Error setting up request.", data: null };
        }
    }
};